import { Box, Stack } from "@mui/material";
import { Icon, Typography } from "@platform/design-system";

import {
  COVERAGE_LABELS,
  FIELD_LABELS,
  STATUS_ICONS,
  STATUS_LABELS,
} from "../labels";

import type {
  CensusFieldCheck,
  CensusLineCheck,
  CoverageEnrollment,
  CoverageType,
} from "../hooks/useCensusValidation";

interface CensusLineChecksProps {
  readonly line: CensusLineCheck;
  readonly employees: number;
}

const isNamedByLine = (coverage: CoverageType, lineName: string): boolean =>
  COVERAGE_LABELS[coverage].toLowerCase() === lineName.toLowerCase();

const pluralEmployees = (count: number): string =>
  count === 1 ? "employee" : "employees";

/**
 * "81 of 180 employees enrolled · 90 waived · 9 not stated". The enrolled
 * count always shows, since it carries the census's total. The other three
 * add up to the rest, so each is left out at zero.
 */
const describeCounts = (
  { enrolled, waived, unreadable, notStated }: CoverageEnrollment,
  employees: number,
): string =>
  [
    `${enrolled} of ${employees} ${pluralEmployees(employees)} enrolled`,
    waived > 0 && `${waived} waived`,
    unreadable > 0 && `${unreadable} unreadable`,
    notStated > 0 && `${notStated} not stated`,
  ]
    .filter(Boolean)
    .join(" · ");

/**
 * The enrolled lives the broker entered on the RFP, a second account of the
 * same number. A disagreement is flagged: one of the two is wrong, and the
 * analyst is the one to find out which before the RFP is quoted.
 */
const BrokerEnrollmentNote = ({
  enrollment: { enrolled, brokerEnrolled },
}: {
  readonly enrollment: CoverageEnrollment;
}): React.JSX.Element | null => {
  if (brokerEnrolled === undefined) {
    return null;
  }

  const note = `The broker entered ${brokerEnrolled} enrolled ${
    brokerEnrolled === 1 ? "life" : "lives"
  } on the RFP`;

  if (brokerEnrolled === enrolled) {
    return (
      <Typography variant="caption" color="textSecondary">
        {note}
      </Typography>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.5}
      data-testid="census-enrollment-disagrees"
    >
      <Icon name="warning" color="warning" size="sm" aria-label="Disagrees" />
      <Typography variant="caption" color="textSecondary">
        {note}
      </Typography>
    </Stack>
  );
};

/**
 * What the census said about each coverage the line covers, one row per
 * coverage. On a line that covers more than one, each row names its coverage,
 * even when the RFP quotes only one of them: "STD: …" under STD / LTD.
 */
const CoverageEnrollmentRows = ({
  lineName,
  enrollment,
  employees,
}: {
  readonly lineName: string;
  readonly enrollment: readonly CoverageEnrollment[];
  readonly employees: number;
}): React.JSX.Element | null => {
  if (enrollment.length === 0) {
    return null;
  }

  const namesCoverages = enrollment.some(
    ({ coverage }) => !isNamedByLine(coverage, lineName),
  );

  return (
    <Stack data-testid="census-line-enrollment">
      {enrollment.map((counts) => (
        <Stack
          key={counts.coverage}
          data-testid={`census-enrollment-${counts.coverage}`}
        >
          <Typography variant="body-medium" color="textSecondary">
            {namesCoverages && `${COVERAGE_LABELS[counts.coverage]}: `}
            {describeCounts(counts, employees)}
          </Typography>
          <BrokerEnrollmentNote enrollment={counts} />
        </Stack>
      ))}
    </Stack>
  );
};

/** How a check's shortfall reads beside it, or nothing when it passed. */
const detailFor = (check: CensusFieldCheck): string => {
  if (check.status === "complete") {
    return "";
  }

  // Neither of these counts members: one is a column we do not read, the
  // other a field no census carries, and "missing for 40 employees" would
  // read as the broker's omission in both cases.
  if (check.status === "unread" || check.status === "notInCensus") {
    return STATUS_LABELS[check.status];
  }

  return `Missing for ${check.missingMembers} ${pluralEmployees(
    check.missingMembers,
  )}`;
};

/** Headings read as a list: `"a", "b"`. */
const quoteAll = (headers: readonly string[]): string =>
  headers.map((header) => `\u201c${header}\u201d`).join(", ");

/**
 * What the census headed this field, and what to head the columns it did not.
 *
 * Both are worth saying and for different reasons: a field read from a
 * heading that means something else is a wrong match nobody would otherwise
 * catch, and a column no heading matched is one the analyst can fix by
 * renaming it, if they are told which name we accept.
 *
 * Both can apply at once. Voluntary life reads a life column and an AD&D
 * column, and a census naming one but not the other needs to hear about each.
 */
const HeadingNote = ({
  check,
}: {
  readonly check: CensusFieldCheck;
}): React.JSX.Element | null => {
  const readFrom = check.readFrom ?? [];
  const toAdd = check.preferredHeaders ?? [];

  if (readFrom.length === 0 && toAdd.length === 0) {
    return null;
  }

  // "No column matched" would be wrong where one of two did, so the mixed
  // case names the column that is missing rather than denying the one found.
  const note =
    readFrom.length === 0
      ? `No column matched. Head it ${quoteAll(toAdd)} and upload again.`
      : `${[
          `Read from ${quoteAll(readFrom)}`,
          toAdd.length > 0 && `no column for ${quoteAll(toAdd)} — add it`,
        ]
          .filter(Boolean)
          .join(", ")}.`;

  return (
    <Typography variant="caption" color="textSecondary">
      {note}
    </Typography>
  );
};

/**
 * One line of coverage and how the census answers each field it needs.
 *
 * A bordered section rather than a card: these sit inside the checks card,
 * and a card within a card doubles the chrome.
 */
export const CensusLineChecks = ({
  line,
  employees,
}: CensusLineChecksProps): React.JSX.Element => (
  <Box
    sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}
    data-testid={`census-line-${line.line}`}
  >
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ mb: 1 }}
    >
      <Stack>
        <Typography variant="subtitle1">{line.line}</Typography>
        <CoverageEnrollmentRows
          lineName={line.line}
          enrollment={line.enrollment}
          employees={employees}
        />
      </Stack>
      <Typography variant="body-medium" color="textSecondary">
        {line.passed}/{line.total} passed
      </Typography>
    </Stack>

    <Stack divider={<Box sx={{ borderBottom: 1, borderColor: "divider" }} />}>
      {line.checks.map((check) => (
        <Stack
          key={check.field}
          data-testid={`census-check-${check.field}`}
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ py: 1 }}
        >
          <Stack direction="row" alignItems="flex-start" gap={1}>
            <Icon
              name={STATUS_ICONS[check.status].name}
              color={STATUS_ICONS[check.status].color}
              size="sm"
              aria-label={STATUS_LABELS[check.status]}
            />
            <Stack>
              <Typography variant="body-medium">
                {FIELD_LABELS[check.field]}
              </Typography>
              <HeadingNote check={check} />
            </Stack>
          </Stack>

          <Typography variant="body-medium" color="textSecondary">
            {detailFor(check)}
          </Typography>
        </Stack>
      ))}
    </Stack>
  </Box>
);
